import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Zap, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import ConnectInstagramButton from "@/components/ConnectInstagramButton";
import SEO from "@/components/SEO";
import api from "@/lib/api";

export default function Login() {
  const navigate = useNavigate();

  return (
    <>
      <SEO
        title="Merchant Login | Checkout DM"
        description="Merchant login is Instagram-exclusive. Checkout DM uses Instagram's official Business Login (OAuth) — we never see your password."
        path="/login"
        image="https://checkoutdm.com/og-image.jpg"
      />
    <div className="grid min-h-screen grid-cols-1 md:grid-cols-2">
      {/* Left brand panel */}
      <div className="relative hidden overflow-hidden bg-sunset-gradient p-10 text-white md:flex md:flex-col md:justify-between">
        <Link
          to="/"
          data-testid="login-back-home"
          className="inline-flex w-max items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-2 text-sm font-semibold backdrop-blur hover:bg-white/20"
        >
          <ArrowLeft size={16} /> Back to home
        </Link>

        <div>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-white/80">
            Merchant sign-in
          </p>
          <h1 className="mt-4 font-display text-6xl leading-[0.95] tracking-tighter">
            Sell inside <br /> your DMs.
          </h1>
          <p className="mt-5 max-w-md text-white/85">
            Connect Instagram once. From that moment, Checkout DM turns
            every message, comment and story reply into a paid order.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-4 text-sm">
          {[
            { icon: MessageCircle, label: "DM-first" },
            { icon: Zap, label: "5-min setup" },
            { icon: ShieldCheck, label: "OAuth secure" },
          ].map((f) => (
            <div
              key={f.label}
              className="rounded-2xl border border-white/20 bg-white/10 p-4 backdrop-blur"
            >
              <f.icon size={18} />
              <p className="mt-3 font-semibold">{f.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Right form panel */}
      <div className="relative flex items-center justify-center bg-cream p-8">
        <Link
          to="/"
          data-testid="login-back-home-mobile"
          className="absolute left-6 top-6 inline-flex items-center gap-2 rounded-full border border-ink/10 bg-white px-4 py-2 text-sm font-semibold md:hidden"
        >
          <ArrowLeft size={16} /> Home
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md rounded-[2rem] border border-ink/10 bg-white p-8 shadow-sm md:p-10"
          data-testid="login-card"
        >
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-hotpink">
            Checkout DM
          </p>
          <h2 className="mt-3 font-display text-3xl leading-tight tracking-tighter md:text-4xl">
            Connect your Instagram to get started.
          </h2>
          <p className="mt-3 text-sm text-ink/60">
            Merchant login is Instagram-exclusive. We use Instagram&apos;s official
            Business Login (OAuth) — we never see your password.
          </p>

          <div className="mt-8">
            <ConnectInstagramButton
              testId="login-connect-instagram"
              className="w-full"
              label="Connect Instagram"
            />
          </div>

          <ul className="mt-8 space-y-3 text-xs text-ink/60">
            <li className="flex items-start gap-2">
              <ShieldCheck size={14} className="mt-0.5 text-hotpink" />
              Read-only permissions unless you enable automations.
            </li>
            <li className="flex items-start gap-2">
              <ShieldCheck size={14} className="mt-0.5 text-hotpink" />
              You can disconnect any time from the dashboard.
            </li>
          </ul>

          <p className="mt-8 border-t border-ink/10 pt-6 text-xs text-ink/50">
            By continuing, you agree to Checkout DM&apos;s{" "}
            <Link to="/terms-of-service" className="underline hover:text-hotpink">
              Terms
            </Link>{" "}
            &amp;{" "}
            <Link to="/privacy-policy" className="underline hover:text-hotpink">
              Privacy Policy
            </Link>
            .
          </p>
        </motion.div>
      </div>
    </div>
     </>
  );
}
